import { useCallback, useState } from 'react';
import type { UseLazyWorkTypeFieldsArguments } from '../../services/types.tsx';
import { useLazyWorkTypesConfigMap } from '../use-lazy-issue-types-fields-map/index.tsx';

/**
 * This hook is responsible for fetching the issue types and its associated fields for a given project key and given work type.
 * It exposes a 1) function to load the data on demand (lazily); 2) a function to check if a specific issue type has a specific associated field.
 * and 3) a function to get the configuration of a specific issue type and field.
 *
 */
export const useLazyAdditionalFieldsConfiguration = ({
	projectKey,
}: UseLazyWorkTypeFieldsArguments): LazyAdditionalFieldsConfigurationLoader => {
	const [selectedWorkTypeId, setSelectedWorkTypeId] = useState<string | number | null>(null);
	const fieldsUtil = useLazyWorkTypesConfigMap({
		projectKey,
	});

	return {
		done: fieldsUtil.done,
		fetchFields: fieldsUtil.load,
		setWorkTypeId: setSelectedWorkTypeId,
		getFieldconfigurationForSelectedWorkType: useCallback(
			<C extends Record<string, unknown>>(fieldId: string) =>
				fieldsUtil.getFieldConfigurationForWorkType<C>(selectedWorkTypeId, fieldId),
			[fieldsUtil, selectedWorkTypeId],
		),
		checkSelectedWorkTypeHasAssociatedField: useCallback(
			(fieldId: string): boolean => fieldsUtil.checkWorkTypeHasField(selectedWorkTypeId, fieldId),
			[fieldsUtil, selectedWorkTypeId],
		),
	};
};

export type LazyAdditionalFieldsConfigurationLoader = {
	/** Indicates if the fields have been loaded. */
	done: boolean;
	/** Load the issue types and their associated fields for the given project key. */
	fetchFields: () => void;
	/** Set the work type id to be used when reading associated fields and configurations. */
	setWorkTypeId: (cardTypeId: string | number | null) => void;
	/** Get the field configuration for the selected work type and field id. When no work type id is defined, returns null */
	getFieldconfigurationForSelectedWorkType: <C extends Record<string, unknown>>(
		fieldId: string,
	) => C | null;
	/** Check if the selected work type has an associated field. */
	checkSelectedWorkTypeHasAssociatedField: (fieldId: string) => boolean;
};
